import CustomModal from '../../containers/Modal'
import { PostDTO } from '../../models/post'
import Post from '../home/Post'
import { useContext, useEffect, useState } from 'react'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import CustomIconButton from '../../components/IconButton'
import VisibilityIcon from '@mui/icons-material/Visibility'
import TextModal from '../../components/Modal/TextModal'
import { FetchResponse } from '../../models/shared'
import { useSnackbar } from 'notistack'
import { dateFormatter } from '../../helpers/utils'
import { CircularProgress, Tooltip } from '@mui/material'
import { PageContext } from '../../context/PageContext'
import CommunitiesService from '../../services/CommunitiesService'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import GoBackButton from '../../components/GoBackButton'
import CustomTable from '../../containers/Table'
import NoResults from '../../containers/NoResults'
import CustomPaper from '../../containers/Paper'
import { AppRoutes } from '../../helpers/routes'

const PendingPostsPage = () => {
    const [pendingPosts, setPendingPosts] = useState<PostDTO[]>()
    const [isPostOpen, setIsPostOpen] = useState<boolean>(false)
    const [mutate, setMutate] = useState<boolean>(false)
    const [selectedPost, setSelectedPost] = useState<PostDTO>()
    const [isActionPostOpen, setIsActionPostOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const { enqueueSnackbar } = useSnackbar()
    const { smallScreen } = useContext(PageContext)
    const { state } = useLocation()
    const { communityId } = useParams<{ communityId: string }>()
    const [actionText, setActionText] = useState<string>('')
    const navigate = useNavigate()

    useEffect(() => {
        if (!communityId) return
        new CommunitiesService().getPendingPosts(communityId).then((response) => {
            setPendingPosts(response.data)
        })
    }, [mutate, communityId])

    const handleActionPost = async (approve: boolean) => {
        let response
        let message

        if (!communityId) return
        if (approve) {
            response = await new CommunitiesService().approvePost(
                communityId,
                selectedPost?.id as string,
            )
            message = 'Publicación aprobada'
        } else {
            response = await new CommunitiesService().rejectPost(
                communityId,
                selectedPost?.id as string,
            )
            message = 'Publicación rechazada'
        }

        if ((response as FetchResponse).status === 200) {
            enqueueSnackbar(message, {
                variant: 'success',
                autoHideDuration: 6000,
            })
            setMutate(!mutate)
            setIsActionPostOpen(false)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    const columnHelper = createColumnHelper<PostDTO>()
    const columns: ColumnDef<PostDTO, any>[] = [
        columnHelper.accessor('dateCreated', {
            header: 'Fecha',
            cell: ({ getValue }) => (
                <div className="text-sm text-secondary w-fit md:w-[80px]">
                    {dateFormatter(new Date(getValue<string>()))}
                </div>
            ),
        }),
        columnHelper.accessor('author', {
            header: 'Autor',
            cell: ({ row }) => (
                <div className="text-sm text-secondary w-fit md:w-[200px]">
                    {row.original.author.firstName + ' ' + row.original.author.lastName}
                </div>
            ),
        }),
    ]

    if (!smallScreen) {
        columns.push(
            columnHelper.accessor('title', {
                header: 'Título de la publicación',
                cell: ({ getValue }) => (
                    <Tooltip title={getValue<string>()}>
                        <div
                            className={`break-words line-clamp-2 text-sm text-secondary w-[600px]`}
                        >
                            {getValue<string>()}
                        </div>
                    </Tooltip>
                ),
            }),
            columnHelper.accessor('image', {
                header: 'Imagen',
                cell: ({ getValue }) => (
                    <div className={`text-sm text-secondary w-[60px]`}>
                        {getValue<string>() !== '' ? 'Sí' : 'No'}
                    </div>
                ),
            }),
            columnHelper.accessor('id', {
                header: 'Acciones',
                cell: ({ row }) => (
                    <div className="flex justify-start w-[110px] -ml-2">
                        <CustomIconButton
                            icon={<VisibilityIcon />}
                            onClick={() => {
                                setSelectedPost(row.original)
                                setIsPostOpen(true)
                            }}
                            title="Ver detalles"
                        />

                        <CustomIconButton
                            icon={<CheckCircleIcon />}
                            onClick={() => {
                                setSelectedPost(row.original)
                                setActionText('Aprobar')
                                setIsActionPostOpen(true)
                            }}
                            title="Aprobar"
                            className="text-success"
                        />

                        <CustomIconButton
                            icon={<CancelIcon />}
                            onClick={() => {
                                setSelectedPost(row.original)
                                setActionText('Rechazar')
                                setIsActionPostOpen(true)
                            }}
                            title="Rechazar"
                            className="text-error"
                        />
                    </div>
                ),
            }),
        )
    } else {
        columns.push(
            columnHelper.accessor('id', {
                header: 'Acciones',
                cell: ({ row }) => (
                    <div className="flex justify-start w-fit md:w-[110px] ml-2">
                        <CustomIconButton
                            icon={<VisibilityIcon />}
                            onClick={() => {
                                setSelectedPost(row.original)
                                setIsPostOpen(true)
                            }}
                            title="Ver detalles"
                        />

                        <CustomIconButton
                            icon={<CheckCircleIcon />}
                            onClick={() => {
                                setSelectedPost(row.original)
                                setIsActionPostOpen(true)
                            }}
                            title="Aprobar"
                            className="text-success"
                        />

                        <CustomIconButton
                            icon={<CancelIcon />}
                            onClick={() => {
                                setSelectedPost(row.original)
                                setIsActionPostOpen(false)
                            }}
                            title="Rechazar"
                            className="text-error"
                        />
                    </div>
                ),
            }),
        )
    }

    return (
        <div className="flex flex-col gap-2 md:gap-4 w-full">
            {state?.goBackPath && (
                <div className="w-fit flex gap-2 items-center">
                    <GoBackButton
                        size={smallScreen ? 'small' : 'medium'}
                        navigate={() =>
                            navigate(state?.goBackPath, {
                                state: {
                                    goBackPath: AppRoutes.communities,
                                },
                            })
                        }
                    />
                    <span className="text-lg md:text-xl font-semibold text-secondary">
                        Volver atrás
                    </span>
                </div>
            )}
            <CustomPaper>
                {pendingPosts ? (
                    pendingPosts.length > 0 ? (
                        <CustomTable
                            data={pendingPosts}
                            columns={columns}
                        />
                    ) : (
                        <NoResults
                            title="No hay publicaciones pendientes de aprobación"
                            subtitle="Acá aparecerán las publicaciones de los miembros de la comunidad luego de ser creadas"
                        />
                    )
                ) : (
                    <div className="flex w-full justify-center">
                        <CircularProgress
                            className="m-2 md:m-5 text-primary"
                            size={64}
                        />
                    </div>
                )}
            </CustomPaper>
            <CustomModal
                isOpen={isPostOpen}
                handleClose={() => setIsPostOpen(false)}
            >
                <div className="w-[50vw] max-h-[70vh]">
                    <Post
                        info={selectedPost as PostDTO}
                        handleClose={() => setIsPostOpen(false)}
                    />
                </div>
            </CustomModal>
            <CustomModal
                isOpen={isActionPostOpen}
                handleClose={() => setIsActionPostOpen(false)}
            >
                <TextModal
                    title={`${actionText} Publicación`}
                    content={`¿Estás seguro de que querés ${actionText.toLowerCase()} esta publicación?`}
                    onAction={async () => {
                        setLoading(true)
                        await handleActionPost(true)
                        setLoading(false)
                    }}
                    onClose={() => setIsActionPostOpen(false)}
                    loading={loading}
                />
            </CustomModal>
        </div>
    )
}

export default PendingPostsPage
